// bibliotecas externas
import { useContext, useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Col,
  Row,
  Spinner,
} from "reactstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// context
import { MatchContext } from "../../providers/match";

// componentes
import CardRoomType from "../../components/RoomType";
import CardError from "../../components/CardError";
import Stepper from "../../components/Stepper";

// services
import { getRoommatesRoom } from "../../utils/services/roommates";
import { getGruposByLogin } from "../../utils/services/group";
import { loginExistsInEvent } from "../../utils/services/login-events";

// store
import { reset } from "../../store/users";

// utils
import { getBackgroundGradient } from "../../utils/color";

export default function RoomSelect() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    idEvent,
    idParticipant,
    setNewContextRefresh,
    setRoomSize,
    setTypeOfRoom,
    t,
    matchConfig,
    i18n,
    errorConfig,
    errorConfigMsg,
  } = useContext(MatchContext);

  // controla a exibição do spinner de carregamento inicial
  const [loadingGetInfo, setLoadingGetInfo] = useState(true);
  // quartos disponíveis
  const [availableRooms, setAvailableRooms] = useState(false);
  // exibir match a partir da data
  const [showMatch, setShowMatch] = useState(true);
  // grupos
  const [group, setgroup] = useState(null);
  // indica se o login informado existe no evento
  const [existsLoginEvent, setExistsLoginEvent] = useState(true);
  // indica se os parâmetros obrigatórios (idEvent/idParticipant) foram informados
  const [existsParams, setExistsParams] = useState(true);

  // busca o quarto do participante; se já houver um quarto vinculado, redireciona para /match
  async function getParticipantInfo() {
    dispatch(reset([]));

    setLoadingGetInfo(true);

    await getRoommatesRoom(idEvent, idParticipant)
      .then((res) => {
        if (res?.data?.roomSize > 0) {
          dispatch(reset([]));
          setRoomSize(res?.data?.roomSize);
          setTypeOfRoom(res?.data?.roomSize);

          navigate({
            pathname: "/match",
            search: `?idEvent=${idEvent}&idPart=${idParticipant}&room=${res.data.roomSize}&idiom=pt-br`,
          });
        } else {
          setLoadingGetInfo(false);
        }
      })
      .catch((err) => {
        alert("Erro na requisição, atualize a página.");
        setLoadingGetInfo(false);
      });
  }

  // busca os grupos vinculados ao login e ao evento
  async function getGroups() {
    await getGruposByLogin(idEvent, idParticipant)
      .then((res) => {
        if (res?.count > 0) {
          setgroup(res.data);
        } else setgroup([]);
      })
      .catch((error) => setgroup(null));
  }

  // recebe um objeto { idioma: texto } e retorna o texto do idioma atual (ou o "default")
  function pickByLang(textByLang) {
    return textByLang[i18n.language] ?? textByLang.default ?? "";
  }

  // retorna, no idioma atual, o texto de "quartos indisponíveis" configurado no evento
  function getTextUnavailable() {
    return pickByLang({
      ptBr: matchConfig?.unavailableRoomsBr,
      en: matchConfig?.unavailableRoomsEn,
      sp: matchConfig?.unavailableRoomsSp,
    });
  }

  // retorna, no idioma atual, o texto de rodapé configurado no evento
  function getTextFooter() {
    return pickByLang({
      ptBr: matchConfig?.footerTextBr,
      en: matchConfig?.footerTextEn,
      sp: matchConfig?.footerTextSp,
    });
  }

  // retorna o texto exibido antes da data de liberação do match
  function getTextOpening() {
    return t("A escolha de quartos estará disponível a partir do dia");
  }

  // verifica se o login existe no evento; se existir, carrega o quarto e os grupos do participante
  async function existsLogin(eventId, loginId) {
    try {
      const res = await loginExistsInEvent(eventId, loginId);

      if (res && res.message === "LOGIN_FOUND") {
        setExistsLoginEvent(true);
        getParticipantInfo();
        getGroups();
      } else {
        setExistsLoginEvent(false);
      }
    } catch (err) {
      setExistsLoginEvent(false);
    }
  }
  useEffect(() => {
    setNewContextRefresh();
    if (idEvent && idParticipant) {
      existsLogin(idEvent, idParticipant);
      setExistsParams(true);
    } else {
      setExistsParams(false);
    }
  }, [idEvent, idParticipant]);

  useEffect(() => {
    if (
      matchConfig?.releaseAt &&
      moment.utc(matchConfig.releaseAt) > moment.utc(new Date())
    ) {
      setShowMatch(false);
    } else setShowMatch(true);
  }, [matchConfig]);

  if (errorConfig) {
    return <CardError message={errorConfigMsg} lang={t} />;
  }

  if (!existsLoginEvent) {
    return <CardError message="LOGIN_NOT_FOUND" lang={t} />;
  }

  if (!existsParams) {
    return <CardError message="PARAMS_NOT_FOUND" lang={t} />;
  }

  if (loadingGetInfo)
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          background: getBackgroundGradient(
            matchConfig?.colorBackground || "#ffffff",
          ),
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spinner color="light" />
      </div>
    );
  else
    return (
      <div
        className="container-main"
        style={{
          background: getBackgroundGradient(
            matchConfig?.colorBackground || "#ffffff",
          ),
          padding: "20px 0",
        }}
      >
        <div className="room-container-main">
          <Stepper
            currentStep={1}
            t={t}
            activeColor={matchConfig?.colorBackground}
          />
          <Card className="room-card-main">
            {showMatch ? (
              <>
                <CardBody className="room-card-body">
                  <CardTitle className="room-card-title">
                    {t("Selecione a hospedagem")}
                    <span className="room-card-subtitle">
                      {t("Escolha o tipo de quarto para continuar")}
                    </span>
                  </CardTitle>
                  <div className="room-container-items">
                    {!availableRooms
                      ? getTextUnavailable() || t("Quartos Indisponíveis")
                      : null}
                    {idEvent && (
                      <>
                        <CardRoomType
                          labelText={t("Quarto duplo")}
                          roomSize={2}
                          id={idEvent}
                          part={idParticipant}
                          setAvailableRooms={setAvailableRooms}
                          group={group}
                        />
                        <CardRoomType
                          labelText={t("Quarto triplo")}
                          roomSize={3}
                          id={idEvent}
                          part={idParticipant}
                          setAvailableRooms={setAvailableRooms}
                          group={group}
                        />
                        <CardRoomType
                          labelText={t("Quarto quádruplo")}
                          roomSize={4}
                          id={idEvent}
                          part={idParticipant}
                          setAvailableRooms={setAvailableRooms}
                          group={group}
                        />
                      </>
                    )}
                  </div>
                </CardBody>
                {getTextFooter() && (
                  <CardFooter className="footer-text">
                    <Row>
                      <Col>
                        <div
                          dangerouslySetInnerHTML={{ __html: getTextFooter() }}
                        />
                      </Col>
                    </Row>
                  </CardFooter>
                )}
              </>
            ) : (
              <div style={{ margin: 20 }}>
                {`${getTextOpening()} ${moment
                  .utc(matchConfig.releaseAt)
                  .format("DD/MM/YYYY")}`}
              </div>
            )}
          </Card>
        </div>
      </div>
    );
}
